import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBookmark, faDownload, faTrash, faTag, faUnlink, faPen } from '@fortawesome/free-solid-svg-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AlertService } from '../../components/alert-service/alert-service';
import { HeaderActionsComponent, HeaderActionsConfig } from '../../components/header-actions/header-actions.component';
import { SearchFilters } from '../../components/search/search.component';
import { ItemListComponent, ListConfig, ListItem } from '../../components/item-list/item-list.component';
import { PaginationComponent } from '../../components/pagination/pagination.component';
import { DateFormatterService } from '../../services/date-formatter.service';
import { ReferenceItem, ReferencesService } from '../../services/references.service';
import { TagManagerComponent, TaggableItem } from '../../components/tag-manager/tag-manager.component';
import { Tag } from '../../services/tags.service';
import { TenantService } from '../../services/tenant.service';

@Component({
  selector: 'app-references',
  standalone: true,
  imports: [
    FontAwesomeModule,
    TranslateModule,
    HeaderActionsComponent,
    ItemListComponent,
    PaginationComponent,
    TagManagerComponent
  ],
  templateUrl: './references.component.html',
  styleUrls: ['./references.component.scss']
})
export class ReferencesComponent implements OnInit {
  references: ReferenceItem[] = [];

  loading = false;

  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;

  currentFilters: SearchFilters = {
    searchTerm: '',
    selectedTag: ''
  };

  headerConfig: HeaderActionsConfig = {
    showAddButton: false,
    showSearch: true,
    searchConfig: {
      showTextSearch: true,
      showTagFilter: true,
      textPlaceholder: 'SEARCH.PLACEHOLDER.REFERENCES',
      tagPlaceholder: 'SEARCH.PLACEHOLDER.TAGS'
    }
  };

  listConfig: ListConfig = {
    itemIcon: faBookmark,
    itemIconClass: 'artifact-icon',
    showMetadata: true,
    showType: false,
    emptyMessage: 'REFERENCES.EMPTY',
    emptyMessageFiltered: 'REFERENCES.EMPTY',
    actions: [
      {
        id: 'link-tag',
        label: 'BUTTON.LINK.TAG',
        icon: faTag,
        cssClass: 'link-tag-item'
      },
      {
        id: 'unlink-tag',
        label: 'BUTTON.UNLINK.TAG',
        icon: faUnlink,
        cssClass: 'unlink-tag-item'
      },
      {
        id: 'rename',
        label: 'BUTTON.RENAME',
        icon: faPen,
        cssClass: 'rename-item'
      },
      {
        id: 'download',
        label: 'BUTTON.DOWNLOAD',
        icon: faDownload,
        cssClass: 'download-item'
      },
      {
        id: 'delete',
        label: 'BUTTON.DELETE',
        icon: faTrash,
        cssClass: 'delete-item'
      }
    ]
  };

  faBookmark = faBookmark;

  @ViewChild(TagManagerComponent) tagManager!: TagManagerComponent;

  constructor(
    private referencesService: ReferencesService,
    private dateFormatter: DateFormatterService,
    private router: Router,
    private alert: AlertService,
    private translate: TranslateService,
    private tenantService: TenantService
  ) {}

  ngOnInit(): void {
    this.loadReferences();
  }

  loadReferences(): void {
    this.loading = true;
    const tagFilter = this.currentFilters.selectedTag?.trim() || undefined;
    const searchFilter = this.currentFilters.searchTerm?.trim() || undefined;

    this.referencesService.listReferences(tagFilter, searchFilter, this.currentPage, this.pageSize).subscribe({
      next: (response: any) => {
        this.references = response.references || [];
        this.totalItems = response.total || 0;
        this.totalPages = response.total_pages || 0;
        this.loading = false;
      },
      error: () => {
        this.references = [];
        this.totalItems = 0;
        this.totalPages = 0;
        this.loading = false;
      }
    });
  }

  get referencesAsListItems(): ListItem[] {
    return this.references.map(reference => ({
      id: reference.id,
      name: reference.name,
      created_date: reference.created_at ? this.dateFormatter.formatDate(reference.created_at) : '',
      created_time: reference.created_at ? this.dateFormatter.formatTime(reference.created_at) : '',
      tag: reference.tag || null
    }));
  }

  onFiltersChanged(filters: SearchFilters): void {
    this.currentFilters = filters;
    this.currentPage = 1;
    this.loadReferences();
  }

  onSearchCleared(): void {
    this.currentFilters.searchTerm = '';
    this.currentPage = 1;
    this.loadReferences();
  }

  onTagFilterCleared(): void {
    this.currentFilters.selectedTag = '';
    this.currentPage = 1;
    this.loadReferences();
  }

  onAllFiltersCleared(): void {
    this.currentFilters = {
      searchTerm: '',
      selectedTag: ''
    };
    this.currentPage = 1;
    this.loadReferences();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadReferences();
  }

  onReferenceClick(item: ListItem): void {
    const reference = this.getReferenceById(item.id);
    if (!reference) {
      return;
    }

    this.router.navigate(this.tenantService.buildRoute(['/references', reference.id]));
  }

  onActionClick(event: { action: string; item: ListItem; event: Event }): void {
    if (event.action === 'download') {
      const reference = this.getReferenceById(event.item.id);
      if (!reference) {
        return;
      }

      this.downloadReference(reference);
      return;
    }

    if (event.action === 'link-tag') {
      this.handleLinkTag(event.item);
      return;
    }

    if (event.action === 'unlink-tag') {
      this.handleUnlinkTag(event.item);
      return;
    }

    if (event.action === 'rename') {
      this.renameReference(event.item.id);
      return;
    }

    if (event.action === 'delete') {
      this.deleteReference(event.item.id);
      return;
    }
  }

  private getReferenceById(id: string | number): ReferenceItem | undefined {
    return this.references.find(reference => reference.id === id);
  }

  private downloadReference(reference: ReferenceItem): void {
    this.referencesService.getReference(String(reference.id)).subscribe({
      next: (response) => {
        const prediction = response.reference?.data?.['prediction'] || {};
        const fileContent = JSON.stringify(prediction, null, 2);
        const blob = new Blob([fileContent], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = reference.file_name || `${this.buildSafeFileName(reference.name || 'reference')}.json`;
        anchor.click();
        window.URL.revokeObjectURL(url);
      },
      error: (err: any) => {
        this.alert.showError(err, this.translate.instant('REFERENCES.DOWNLOAD_ERROR'));
      }
    });
  }

  private deleteReference(id: string | number): void {
    if (!confirm(this.translate.instant('REFERENCES.DELETE_CONFIRM'))) {
      return;
    }

    this.referencesService.deleteReference(String(id)).subscribe({
      next: () => {
        this.alert.showSuccess(this.translate.instant('REFERENCES.DELETE_SUCCESS'));
        this.loadReferences();
      },
      error: (err: any) => {
        this.alert.showError(err, this.translate.instant('REFERENCES.DELETE_ERROR'));
      }
    });
  }

  private renameReference(id: string | number): void {
    const reference = this.getReferenceById(id);
    if (!reference) {
      return;
    }

    const nextName = prompt(this.translate.instant('REFERENCES.RENAME_PROMPT'), reference.name || '');
    if (nextName === null) {
      return;
    }

    const trimmedName = nextName.trim();
    if (!trimmedName) {
      this.alert.showError(this.translate.instant('REFERENCES.RENAME_ERROR'));
      return;
    }

    this.referencesService.renameReference(String(id), trimmedName).subscribe({
      next: () => {
        this.alert.showSuccess(this.translate.instant('REFERENCES.RENAME_SUCCESS'));
        this.loadReferences();
      },
      error: (err: any) => {
        this.alert.showError(err, this.translate.instant('REFERENCES.RENAME_ERROR'));
      }
    });
  }

  handleLinkTag(item: ListItem): void {
    const taggableItem: TaggableItem = {
      id: String(item.id),
      name: item.name,
      tag: item.tag as Tag
    };
    this.tagManager?.openLinkModal(taggableItem);
  }

  handleUnlinkTag(item: ListItem): void {
    const taggableItem: TaggableItem = {
      id: String(item.id),
      name: item.name,
      tag: item.tag as Tag
    };
    this.tagManager?.openUnlinkModal(taggableItem);
  }

  onTagChanged(): void {
    this.loadReferences();
  }

  private buildSafeFileName(name: string): string {
    return name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'reference';
  }
}
